/**
 * The provider boundary.
 *
 * Everything in this folder is INTERFACES plus one in-memory implementation.
 * There is no HTTP client, no SDK, and no Dexie import anywhere below — and
 * that is not a style preference, it is the mechanism. See ADR 0009.
 *
 * WHY NO DEXIE HERE
 * -----------------
 * A provider that could reach the database would be a second data layer. It
 * could write business rows outside a transaction, which is precisely the failure
 * ADR 0005 exists to prevent, and it would make the app untestable offline. The
 * engine owns the local transaction; the provider owns bytes.
 *
 * WHY SYNC AND BACKUP ARE SEPARATE INTERFACES
 * -------------------------------------------
 * They are different operations with different units — one mutation versus one
 * whole-database export. Forcing them together is the error this split prevents:
 * a provider chosen because it is good at one of them then cripples the other.
 * The test is concrete, and is in the contract suite: backup to one provider and
 * sync to another must work with no code change in either direction.
 */
import type { SyncChange, SyncCapabilities, SyncCursor, ProviderId } from '../../domain/sync/changeModel';

/**
 * Why a provider call failed.
 *
 * A closed set, because the engine has to branch on these. An adapter that
 * invents its own codes forces the engine to string-match, which is how a
 * provider ends up silently treated as "retry forever".
 */
export type ProviderErrorCode =
  /** Not signed in, or the grant expired. Recoverable by the user. */
  | 'NOT_AUTHENTICATED'
  /** Signed in, but the account is not permitted to touch this business. */
  | 'FORBIDDEN'
  /** No network, DNS failure, offline. The common case, and never fatal. */
  | 'UNREACHABLE'
  /** The provider is up and said no. The identical request will not help. */
  | 'REJECTED'
  /** The remote history is older or newer than this client expects. */
  | 'VERSION_MISMATCH'
  /** Storage is full, or the account is over quota. */
  | 'QUOTA_EXCEEDED'
  /** The provider cannot do what was asked — no transactions, no delete, etc. */
  | 'UNSUPPORTED_OPERATION'
  /** Anything else. The message is preserved; the code is not. */
  | 'UNKNOWN';

export class ProviderError extends Error {
  constructor(
    readonly code: ProviderErrorCode,
    message: string,
    /**
     * TRUE when retrying the identical request could plausibly succeed.
     *
     * This flag is the difference between a retry loop and a user-facing error,
     * so it is part of the contract rather than something each adapter decides by
     * throwing a different code. A 403 and a 503 are both failures; only one is
     * worth sending back unchanged.
     */
    readonly retryable: boolean,
    options?: { readonly cause?: unknown },
  ) {
    super(message, options);
    this.name = 'ProviderError';
  }
}

/** Where a connection stands. Never a boolean: "connecting" and "error" are states. */
export type ConnectionState =
  | 'disconnected'
  | 'connecting'
  | 'connected'
  | 'unauthenticated'
  | 'error';

/**
 * Connection state, and enough identity to show a user WHICH account is connected.
 *
 * The identity matters more than it looks. A user with two cloud accounts cannot
 * tell which one the app is writing to, and "it is not the one I expected" is the
 * most damaging sync bug there is.
 */
export interface ProviderStatus {
  readonly state: ConnectionState;
  /** A human label for the connected account, e.g. "shop@example.invalid". */
  readonly accountLabel?: string;
  /** The businesses this connection is authorised for. */
  readonly authorisedBusinessIds: readonly string[];
  readonly message?: string;
  readonly lastCheckedAt?: string;
}

/**
 * A remote destination for BYTES.
 *
 * The lowest common denominator, shared by sync and backup so a file-storage
 * provider can serve both without two adapters. Deliberately small: anything
 * richer here becomes an assumption the sync engine must then work around.
 */
export interface CloudStorage {
  getStatus(): Promise<ProviderStatus>;
  /** True when the storage is reachable. Cheap; may be cached by the engine. */
  isReachable(): Promise<boolean>;
}

/**
 * Authentication, kept separate from the transports.
 *
 * A provider authenticated against one account and syncing another is a real
 * configuration, and conflating the two makes it inexpressible.
 */
export interface CloudAuthProvider {
  /**
   * Begin connecting. MUST request the least privilege the provider offers.
   *
   * An implementation that asks for a whole drive when a single app folder would
   * do is a defect, not a limitation: DNR Vyapar has no business reading a
   * customer's unrelated files.
   */
  connect(): Promise<ProviderStatus>;
  /** Sign out and forget the grant. Never deletes remote data. */
  disconnect(): Promise<void>;
  getStatus(): Promise<ProviderStatus>;
}

/**
 * A change the provider could not hand over, and why.
 *
 * WHY THIS IS PART OF THE CONTRACT
 * ------------------------------
 * A download that silently drops a record is indistinguishable from a download
 * that had nothing to say: the device looks healthy while it is missing data.
 * Providers therefore have to NAME what they refused, and the engine has to
 * carry that name into the status the user sees.
 */
export interface SkippedChange {
  /** Null when the row did not carry a usable id. */
  readonly changeId: string | null;
  /** Machine-readable, e.g. `UPSERT_WITHOUT_PAYLOAD`. */
  readonly reason: string;
  /** One line that says what is wrong with the row. */
  readonly detail: string;
}

/**
 * SYNCHRONISATION transport. Different from `BackupProvider` on purpose.
 */
export interface SyncProvider extends CloudStorage {
  readonly id: ProviderId;
  readonly label: string;
  /** What this provider can and cannot do. The engine branches on it. */
  capabilities(): SyncCapabilities;

  /**
   * Send changes the local outbox is holding.
   *
   * MUST be idempotent on `changeId`: a repeated id is a no-op that still
   * succeeds. A provider that appends on a repeat will duplicate invoices and
   * payments the first time a response is lost.
   *
   * Returns the ids it accepted so the engine can acknowledge exactly those.
   */
  pushChanges(input: {
    readonly businessId: string;
    readonly deviceId: string;
    readonly changes: readonly SyncChange[];
  }): Promise<{ readonly accepted: readonly string[] }>;

  /**
   * Fetch changes after `cursor`.
   *
   * MUST signal "too old" through `requiresSnapshot`, not by quietly resending
   * the whole log. A device offline for a month needs a snapshot, and returning
   * everything from the beginning is how duplicates get created.
   */
  pullChanges(input: {
    readonly businessId: string;
    readonly deviceId: string;
    readonly cursor: SyncCursor | null;
    readonly limit: number;
  }): Promise<{
    readonly changes: readonly SyncChange[];
    /** Where to resume. Null when the history was compacted away. */
    readonly nextCursor: SyncCursor | null;
    /** True when the requested cursor is older than what is retained. */
    readonly requiresSnapshot: boolean;
    /**
     * Rows the provider refused to hand over, with the reason.
     *
     * MUST be reported rather than thrown: one unreadable legacy row must not
     * freeze every later change for this business. MUST be empty, never
     * omitted, when nothing was skipped — an absent list and an empty one look
     * the same to a caller and only one of them is honest.
     */
    readonly skipped: readonly SkippedChange[];
  }>;

  /** A complete, validated current state. The long-offline recovery path. */
  fetchSnapshot(input: {
    readonly businessId: string;
  }): Promise<{
    readonly changes: readonly SyncChange[];
    readonly cursor: SyncCursor;
    /** Rows the snapshot could not carry. Same rule as `pullChanges`. */
    readonly skipped: readonly SkippedChange[];
  }>;

  /**
   * Record that this device has applied up to `cursor`.
   *
   * This is what makes compaction safe: a change may be deleted only once every
   * active device has acknowledged passing it.
   */
  acknowledge(input: {
    readonly businessId: string;
    readonly deviceId: string;
    readonly cursor: SyncCursor;
  }): Promise<void>;

  /**
   * The oldest cursor any ACTIVE device still needs.
   *
   * The engine compacts only above this. A device unseen for months is not
   * forgotten on its own; the retention rule decides that.
   */
  oldestNeededCursor(input: { readonly businessId: string }): Promise<SyncCursor | null>;

  /**
   * Diagnostic: check whether the server's latest-per-entity snapshot for a
   * business includes a business entity row.
   *
   * Used exclusively by the business-recovery workflow: when the sync log has
   * been pruned and the snapshot lacks the business row, this tells the client
   * whether a re-push is needed before attempting recovery.
   *
   * MUST be null-safe: a provider that cannot answer (e.g. file-storage
   * providers that do not prune) returns null. A network error, missing
   * function, permission error, or malformed response must NOT be interpreted
   * as `exists=false` — it must surface as a ProviderError so the caller can
   * distinguish "definitely missing" from "unable to tell".
   */
  checkBusinessRowExists?(input: {
    readonly businessId: string;
  }): Promise<{ readonly exists: boolean; readonly totalChanges: number; readonly cursor: string } | null>;
}

/**
 * BACKUP transport. Whole-database, validated, restore-safe.
 *
 * Carries the EXISTING versioned backup format unchanged. A second, cloud-shaped
 * backup format would mean a restore that validates one way on the web and
 * another on a NAS, and the validated format already exists and is tested.
 */
export interface BackupProvider extends CloudStorage {
  readonly id: ProviderId;
  readonly label: string;
  /** Whether this destination can delete. Object-locked buckets cannot. */
  readonly canDelete: boolean;

  uploadBackup(input: {
    readonly fileName: string;
    readonly json: string;
    readonly takenAt: string;
  }): Promise<{ readonly remoteName: string }>;

  /** Returns TEXT, never a parsed object, so validation stays the caller's. */
  downloadBackup(input: { readonly remoteName: string }): Promise<{ readonly json: string }>;

  listBackups(): Promise<
    ReadonlyArray<{ readonly remoteName: string; readonly sizeBytes: number; readonly updatedAt: string }>
  >;

  /** Refuses, with a clear message, when the provider cannot delete. */
  deleteBackup(input: { readonly remoteName: string }): Promise<void>;
}

/** Everything a provider registration carries. */
export interface ProviderRegistration {
  readonly id: ProviderId;
  readonly label: string;
  /** What kind of provider this is. A provider may offer both. */
  readonly kinds: readonly ('sync' | 'backup')[];
  /**
   * A one-line description of the ACCOUNTING MODEL, not the marketing one.
   * "Transactional row store", "File storage only", so a user can tell a managed
   * cloud from their own NAS without being misled.
   */
  readonly model: string;
  readonly create: () => SyncProvider | BackupProvider;
}

